


import React, { useState } from 'react';
import { toast } from 'sonner';
import { useCurrentUser } from '@/hooks/useUser';
import {
  useAddReview,
  useEditReview,
  useDeleteReview,
} from '@/hooks/useReviews';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import StarRating from './StarRating';

interface ReviewFormProps {
  productId: string;
  productTitle: string;
  existingReview?: any;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  productId,
  productTitle,
  existingReview,
  onSuccess,
  onCancel,
}) => {
  const { data: userProfile, isLoading: userLoading } = useCurrentUser();

  const addReview = useAddReview();
  const editReview = useEditReview();
  const deleteReview = useDeleteReview();

  const [rating, setRating] = useState(existingReview?.rating || 0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState(existingReview?.comment || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const finalRating = hoverRating || rating;

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!userProfile) {
      toast.error('You must be logged in to submit a review.');
      return;
    }

    if (rating === 0) {
      toast.error('Please select a rating.');
      return;
    }

    if (comment.trim().length < 10) {
      toast.error('Review must be at least 10 characters.');
      return;
    }

    if (existingReview) {
      editReview.mutate({
        reviewId: existingReview.id,
        data: { rating, comment, productId },
      });
    } else {
      addReview.mutate({
        productId,
        userId: userProfile.uid,
        userName: userProfile.displayName || 'Anonymous',
        rating,
        comment,
      });
    }

    onSuccess?.();
  };

  if (userLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary-600 rounded-full" />
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="text-center py-8 text-primary-700">
        <p>Unable to load profile. Try again.</p>
      </div>
    );
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="space-y-8 bg-background-soft p-6 rounded-2xl border border-[rgba(45,45,45,0.08)]"
      >
        {/* Book Info */}
        <div className="bg-background rounded-xl p-4 border border-[rgba(45,45,45,0.06)]">
          <p className="text-sm text-primary-700">Writing a review for:</p>
          <p className="font-semibold text-primary-900 mt-1">{productTitle}</p>
        </div>

        {/* Rating */}
        <div>
          <label className="block text-sm font-medium text-primary-800 mb-3">
            Rating *
          </label>

          <StarRating rating={rating} setRating={handleRatingClick} />
     
        </div>

        {/* Comment */}
        <div>
          <label className="block text-sm font-medium text-primary-800 mb-2">
            Your Review *
          </label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            className="w-full px-4 py-3 bg-background rounded-xl border border-[rgba(45,45,45,0.06)] focus:ring-2 focus:ring-primary-300 focus:border-primary-400 transition-all"
            placeholder="Share your experience..."
          />
          <p className="text-xs mt-2 text-primary-700/70">
            {comment.length < 10
              ? `Minimum 10 characters (${comment.length}/10)`
              : `${comment.length} characters`}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-5 border-t border-[rgba(45,45,45,0.06)]">
          {existingReview && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 rounded-xl bg-red-700 text-white hover:bg-red-700 transition"
            >
              Delete Review
            </button>
          )}

          {/* {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 rounded-xl bg-background text-primary-900 border border-[rgba(45,45,45,0.1)] hover:bg-background-soft transition"
            >
              Cancel
            </button>
          )} */}

          <button
            type="submit"
            className="px-5 py-2.5 rounded-xl font-semibold bg-primary-600 text-white hover:bg-primary-700 transition"
            disabled={addReview.isPending || editReview.isPending}
          >
            {addReview.isPending || editReview.isPending
              ? existingReview
                ? 'Updating...'
                : 'Submitting...'
              : existingReview
              ? 'Update Review'
              : 'Submit Review'}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          deleteReview.mutate({ reviewId: existingReview.id, productId });
          onSuccess?.();
        }}
        title="Delete Review?"
        message="This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </>
  );
};

export default ReviewForm;
